import React, { useState } from 'react';
import { ArrowDownToLineIcon, ExternalLinkIcon, ListIcon } from 'lucide-react';
import { Book } from '../types';
import { books } from '../data/books';
import { toDirect, toView } from '../data/links';
import { SectionHeading } from './SectionHeading';
import { BookDrawer } from './BookDrawer';
const pad = (value: number) => String(value).padStart(2, '0');
function Spine({
  book
}: {
  book: Book;
}) {
  return <div className="flex aspect-[3/4] w-[62%] max-w-[190px] flex-col justify-between p-4" style={{
    backgroundColor: '#1F3A5F',
    borderTop: `4px solid ${book.accent}`
  }} data-id="element-100" data-mp="2fmny5z4bw1bf">
      <span className="tnum font-sans text-[10px] tracking-[0.16em]" style={{
      color: book.accent
    }} data-id="element-101" data-mp="2ogskc58iookm">
        {pad(book.number)}
      </span>
      <span className="font-display text-[15px] leading-tight text-[#F6F1E4]" data-id="element-102" data-mp="2tgyfvwhwqv7z">{book.title}</span>
    </div>;
}
export function BookGrid() {
  const [openBook, setOpenBook] = useState<Book | null>(null);
  return <section aria-labelledby="books-heading" id="books" className="scroll-mt-24" data-id="element-103" data-mp="33anrn6t0y5tp">
      <SectionHeading index="02" title="The twelve books" note="Each one downloads whole, at print resolution. Open the project list to find a single build without paging through the file." data-id="element-104" data-mp="3p7vv3ejnf1c" />

      <div className="mt-8 grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-3" data-id="element-105" data-mp="3lwxfj7ptj6th">
        {books.map(book => {
        const view = toView(book.file);
        const dl = toDirect(book.file);
        return <article key={book.number} className="flex flex-col bg-surface" data-id="element-106" data-mp="hi7cx9tz5hb0">
              <div className="h-[3px] w-full" style={{
            backgroundColor: book.accent
          }} data-id="element-107" data-mp="crzcdhma10zu" />
              <div className="flex flex-1 flex-col p-6" data-id="element-108" data-mp="cpmjwaiw788j">
                <div className="flex items-baseline justify-between gap-3" data-id="element-109" data-mp="8pm68s3mb12j">
                  <p className="tnum font-sans text-[11px] tracking-[0.16em]" style={{
                color: book.accent
              }} data-id="element-110" data-mp="158js95rjugbm">
                    BOOK {pad(book.number)}
                  </p>
                  <p className="tnum font-sans text-[11px] text-ink/40" data-id="element-111" data-mp="2upfa2tcnehzn">
                    {book.projectCount} projects
                  </p>
                </div>

                <h3 className="mt-3 font-display font-semibold text-[22px] leading-tight text-ink" data-id="element-112" data-mp="2znqkb56uuuie">{book.title}</h3>
                <p className="mt-1 font-sans text-[15px] leading-snug text-ink/60" data-id="element-113" data-mp="2kxrqfemc258q">{book.subtitle}</p>

                <div className="mt-5 flex justify-center border-y border-rule/70 py-5" data-id="element-114" data-mp="2pwchoojlxel1">
                  {book.cover ? <img src={book.cover} alt={`Cover of Book ${pad(book.number)}, ${book.title}`} loading="lazy" width={600} height={848} className="h-auto w-[72%] max-w-[215px] border border-rule shadow-[0_1px_0_rgba(31,58,95,0.10)]" data-id="element-115" data-mp="2gyjyvaeofosw" /> : <Spine book={book} data-id="element-116" data-mp="58q2zodrt2as" />}
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 pt-5" data-id="element-117" data-mp="1pq8xkq435uvp">
                  <a href={view || undefined} target="_blank" rel="noreferrer" aria-disabled={!view} className={`inline-flex items-center gap-2 px-3.5 py-2.5 font-sans text-[13px] leading-none text-onaccent transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${view ? 'bg-green hover:bg-[#33684D]' : 'cursor-not-allowed bg-locked/40'}`} onClick={event => {
                if (!view) event.preventDefault();
              }} data-id="element-118" data-mp="1krpc0qteml3v">
                    <ExternalLinkIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} data-id="element-119" data-mp="o1y7rzvb9jkv" />
                    Open book
                  </a>
                  <button type="button" onClick={() => setOpenBook(book)} className="inline-flex items-center gap-2 border border-rule px-3.5 py-2.5 font-sans text-[13px] leading-none text-ink transition-colors duration-150 ease-out hover:border-ink/45 hover:bg-ink/[0.05] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-surface" data-id="element-120" data-mp="kj94w0t9q0um">
                    <ListIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} data-id="element-121" data-mp="1qwucqsgnt9e" />
                    All {book.projectCount} projects
                  </button>
                  {dl ? <a href={dl} className="inline-flex items-center gap-1.5 font-sans text-[12.5px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-122" data-mp="bp2up3ymcrc5">
                      <ArrowDownToLineIcon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-123" data-mp="21b2wvnnq3arw" />
                      Download
                    </a> : null}
                </div>
              </div>
            </article>;
      })}
      </div>

      <BookDrawer book={openBook} onClose={() => setOpenBook(null)} data-id="element-124" data-mp="1nas5mjii3u67" />
    </section>;
}